import axios from "axios";
import toast from "react-hot-toast";
import { APPLICATION_KEY } from "./Configurations/Urls";
import useAuthStore from "./Zustand/Store/useAuthStore";
import { getLoginPathAfterLogout } from "./Utils/studentPortalPath";

function mergeAuthHeaders(initHeaders) {
  const headers = new Headers();

  if (initHeaders) {
    if (initHeaders instanceof Headers) {
      initHeaders.forEach((value, key) => headers.set(key, value));
    } else if (Array.isArray(initHeaders)) {
      initHeaders.forEach(([key, value]) => headers.set(key, value));
    } else {
      Object.entries(initHeaders).forEach(([key, value]) => {
        if (value != null) headers.set(key, String(value));
      });
    }
  }

  const token = localStorage.getItem("token");
  if (token && !headers.has("Authorization") && !headers.has("authorization")) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  if (APPLICATION_KEY && !headers.has("X-Application-Key")) {
    headers.set("X-Application-Key", APPLICATION_KEY);
  }

  try {
    const workspace = localStorage.getItem("ems.activeWorkspace");
    if (workspace && !headers.has("X-EMS-Workspace")) {
      headers.set("X-EMS-Workspace", workspace);
    }
  } catch {
    // ignore storage access
  }

  return headers;
}

function isInactiveOrgAccessMessage(msg) {
  const msgLower = typeof msg === "string" ? msg.toLowerCase() : "";
  return (
    msgLower.includes("inactive for this organization") ||
    msgLower.includes("disabled for your organization") ||
    msgLower.includes("not enabled for") ||
    msgLower.includes("not linked to this organization") ||
    msgLower.includes("not linked to any organization") ||
    msgLower.includes("not active for your account") ||
    msgLower.includes("organization is not available for this application") ||
    msgLower.includes("subscription is inactive") ||
    msgLower.includes("subscription has expired")
  );
}

function shouldForceLogoutFromAccessError(errorLike) {
  const status = errorLike?.response?.status;
  if (status !== 403) return false;
  const data = errorLike?.response?.data || {};
  if (data.force_logout === true || data.access_revoked === true) {
    if (data.upgrade_required === true || data.billing_only === true) {
      return false;
    }
    return true;
  }
  return isInactiveOrgAccessMessage(data.message);
}

function forceLogoutOnInactiveAccess(msg) {
  if (msg) toast.error(msg);
  const loginPath = getLoginPathAfterLogout(useAuthStore.getState().userData);
  useAuthStore.getState().logout();
  if (typeof window !== "undefined") {
    const path = String(window.location.pathname || "");
    if (path !== loginPath && !path.startsWith(`${loginPath}/`)) {
      window.location.href = loginPath;
    }
  }
}

/**
 * Attaches JWT and product key (ems) to every axios and fetch request.
 * Organization-scoped routes require auth:applicationusers + X-Application-Key.
 */
export function setupAxiosApplicationKey() {
  axios.interceptors.request.use((config) => {
    config.headers = config.headers || {};

    const token = localStorage.getItem("token");
    if (token && !config.headers.Authorization && !config.headers.authorization) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    if (APPLICATION_KEY && !config.headers["X-Application-Key"]) {
      config.headers["X-Application-Key"] = APPLICATION_KEY;
    }

    try {
      const workspace = localStorage.getItem("ems.activeWorkspace");
      if (workspace && !config.headers["X-EMS-Workspace"]) {
        config.headers["X-EMS-Workspace"] = workspace;
      }
    } catch {
      // ignore storage access
    }

    return config;
  });

  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const status = error.response?.status;
      const url = error.config?.url ?? "";
      const msg = error.response?.data?.message;
      const msgLower = typeof msg === "string" ? msg.toLowerCase() : "";

      const upgradeRequired =
        status === 403 &&
        (error.response?.data?.upgrade_required === true ||
          error.response?.data?.billing_only === true ||
          msgLower.includes("upgrade to a paid plan") ||
          msgLower.includes("trial or subscription has ended"));

      if (
        upgradeRequired &&
        !url.includes("/api/auth/login") &&
        !url.includes("/auth/login") &&
        !url.includes("/ems/billing")
      ) {
        toast.error(msg || "Please upgrade your plan to continue.");
        localStorage.setItem("ems_billing_only", "1");
        if (
          typeof window !== "undefined" &&
          !String(window.location.pathname).startsWith("/billing")
        ) {
          window.location.href = "/billing/plans?upgrade=1";
        }
        return Promise.reject(error);
      }

      if (
        shouldForceLogoutFromAccessError(error) &&
        !url.includes("/api/auth/login") &&
        !url.includes("/auth/login")
      ) {
        forceLogoutOnInactiveAccess(msg);
      }

      return Promise.reject(error);
    }
  );

  if (typeof window !== "undefined" && !window.__emsFetchPatched) {
    const originalFetch = window.fetch.bind(window);
    window.fetch = async (input, init = {}) => {
      const headers = mergeAuthHeaders(init.headers);
      const response = await originalFetch(input, { ...init, headers });

      const url = typeof input === "string" ? input : input?.url || "";
      const isLoginUrl =
        String(url).includes("/api/auth/login") ||
        String(url).includes("/auth/login");

      if (!isLoginUrl && response.status === 403) {
        try {
          const clone = response.clone();
          const data = await clone.json();
          const fakeError = { response: { status: 403, data } };
          if (shouldForceLogoutFromAccessError(fakeError)) {
            forceLogoutOnInactiveAccess(data?.message);
          }
        } catch {
          // ignore non-JSON
        }
      }

      return response;
    };
    window.__emsFetchPatched = true;
  }
}
